import { call } from '@/lib/api';
import { queryClient } from '@/lib/query';
import { useQuery } from '@tanstack/react-query';

export type TRatesResponse = {
  timestamp: number;
  quotes: Record<string, number>;
};

export const RATES_QUERY_KEY = ['stats', 'rates'] as const;

export const useRates = () => {
  return useQuery({
    queryKey: RATES_QUERY_KEY,
    staleTime: 1000 * 60 * 60 * 24,
    queryFn: () =>
      call<TRatesResponse>('stats/rates').then((res) => res.json()),
  });
};

export const convertToEur = (amount: number, currency: string): number => {
  const ratesData = queryClient.getQueryData<TRatesResponse>(RATES_QUERY_KEY);

  if (!ratesData?.quotes) {
    return NaN;
  }

  return amount / ratesData.quotes[`EUR${currency}`];
};

/**
 * Convert amount between any two currencies using EUR as the base.
 * All rates in the API are relative to EUR (e.g., EURUSD = 1.10 means 1 EUR = 1.10 USD)
 *
 * @param amount - The amount to convert
 * @param fromCurrency - Source currency code (e.g., 'USD', 'EUR', 'GBP')
 * @param toCurrency - Target currency code (e.g., 'EUR', 'USD', 'GBP')
 * @param rates - Optional rates data (if not provided, uses cached data)
 * @returns Converted amount, or NaN if rates not available
 */
export const convertCurrency = (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates?: TRatesResponse,
): number => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  const ratesData =
    rates || queryClient.getQueryData<TRatesResponse>(RATES_QUERY_KEY);

  if (!ratesData?.quotes) {
    return NaN;
  }

  // Convert to EUR first, then to target currency
  // All rates are EUR-based: EURUSD means 1 EUR = X USD
  let eurAmount: number;

  if (fromCurrency === 'EUR') {
    eurAmount = amount;
  } else {
    const fromRate = ratesData.quotes[`EUR${fromCurrency}`];
    if (!fromRate) return NaN;
    eurAmount = amount / fromRate;
  }

  if (toCurrency === 'EUR') {
    return eurAmount;
  }

  const toRate = ratesData.quotes[`EUR${toCurrency}`];
  if (!toRate) return NaN;

  return eurAmount * toRate;
};

/**
 * Get the exchange rate between two currencies
 */
export const getExchangeRate = (
  fromCurrency: string,
  toCurrency: string,
  rates?: TRatesResponse,
): number => {
  if (fromCurrency === toCurrency) {
    return 1;
  }

  const ratesData =
    rates || queryClient.getQueryData<TRatesResponse>(RATES_QUERY_KEY);

  if (!ratesData?.quotes) {
    return NaN;
  }

  // Calculate rate via EUR
  const fromRate =
    fromCurrency === 'EUR' ? 1 : ratesData.quotes[`EUR${fromCurrency}`];
  const toRate =
    toCurrency === 'EUR' ? 1 : ratesData.quotes[`EUR${toCurrency}`];

  if (!fromRate || !toRate) return NaN;

  return toRate / fromRate;
};
